// /admin/empresas/:empresaId/presupuesto/nuevo
// Wizard 3 pasos: Header → Items → Revisión.
// Al finalizar: crea presupuesto + items en BD, genera HTML brandeado en Storage,
// y redirige a /admin/presupuestos/:id para enviar.

import { useEffect, useMemo, useState } from 'react'

// Helper: leer borrador desde localStorage sin crashear si el JSON está corrupto.
function loadDraft(key) {
  if (!key || typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    // Validación mínima — descarta si no tiene shape esperado
    if (!parsed || typeof parsed !== 'object') return null
    if (!Array.isArray(parsed.items)) return null
    return parsed
  } catch {
    return null
  }
}

function fmtHace(iso) {
  if (!iso) return ''
  const diff = Date.now() - new Date(iso).getTime()
  const min = Math.floor(diff / 60000)
  if (min < 1) return 'hace instantes'
  if (min < 60) return `hace ${min} min`
  const hs = Math.floor(min / 60)
  if (hs < 24) return `hace ${hs}h`
  return new Date(iso).toLocaleString('es-UY', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const PASOS = ['Header', 'Items', 'Revisión']

export default function PresupuestoNuevo() {
  const { empresaId } = useParams()
  const [searchParams] = useSearchParams()
  const editarId = searchParams.get('edit')  // si viene ?edit=<uuid> → modo edición
  const nav = useNavigate()
  const [numeroEditando, setNumeroEditando] = useState(null)  // número del presupuesto que estamos editando (para mostrar)

  const [empresa, setEmpresa] = useState(null)
  const [direcciones, setDirecciones] = useState([])
  const [edificios, setEdificios] = useState([])
  const [productos, setProductos] = useState([])
  const [cargandoCatalogo, setCargandoCatalogo] = useState(true)

  // Persistencia del borrador en localStorage (por empresa) — resiste refresh y navegación.
  // Clave incluye empresaId para no mezclar borradores entre empresas.
  const draftKey = empresaId ? `limpack_presup_borrador_${empresaId}` : null
  const draftSaved = draftKey ? loadDraft(draftKey) : null

  const [paso, setPaso] = useState(draftSaved?.paso ?? 0)
  const [header, setHeader] = useState(draftSaved?.header ?? {
    modalidad: 'suscripcion',
    ocultar_total: false,
    edificio_id: '',
    facturacion_con_rut: null,
    fecha: new Date().toISOString().slice(0, 10),
    validez_dias: 15,
    condiciones_pago: 'Pago contado contra entrega',
    condiciones_entrega: 'Entregas mensuales en su dirección, en el día de su barrio',
    notas_internas: '',
    notas_cliente: '',
    descuento_pct: 0,
  })
  const [items, setItems] = useState(draftSaved?.items ?? [])
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState(null)
  const [borradorRestaurado, setBorradorRestaurado] = useState(!!draftSaved)

  // Auto-guardar en cada cambio (React debouncea con dependencies + write local es barato).
  useEffect(() => {
    if (!draftKey) return
    // No persistir si es estado inicial vacío (evita crear un borrador fantasma al entrar)
    if (paso === 0 && items.length === 0 && !header.notas_internas && !header.notas_cliente) return
    try {
      localStorage.setItem(draftKey, JSON.stringify({
        paso, header, items, guardadoEn: new Date().toISOString(),
      }))
    } catch { /* quota exceeded, ignore */ }
  }, [draftKey, paso, header, items])

  useEffect(() => {
    (async () => {
      const [{ data: e }, { data: dirs }, { data: prods }] = await Promise.all([
        supabase.from('empresas').select('id, razon_social, rut, tipo, facturacion_modo').eq('id', empresaId).single(),
        supabase.from('empresas_direcciones').select('id, alias').eq('empresa_id', empresaId).eq('activa', true).order('alias'),
        supabase.from('productos').select('id, sku, nombre, marca, presentacion, precio_publico, iva_pct').is('deleted_at', null).eq('activo', true).order('nombre'),
      ])
      setEmpresa(e)
      setDirecciones(dirs || [])
      setProductos(prods || [])
      const { data: eds } = e?.tipo === 'administradora'
        ? await supabase.from('edificios').select('id, nombre, razon_social, rut, facturacion_email, direccion, apto').eq('empresa_id', empresaId).eq('activo', true).order('nombre')
        : { data: [] }
      setEdificios(eds || [])

      // Modo edición: precargar el presupuesto existente.
      // Solo permite editar borradores. Si viene aceptado/enviado, avisamos.
      if (editarId) {
        const { data: p, error: pErr } = await supabase
          .from('presupuestos')
          .select('*, items:presupuestos_items(*)')
          .eq('id', editarId)
          .single()
        if (pErr || !p) {
          setError('No se pudo cargar el presupuesto a editar: ' + (pErr?.message || 'no existe'))
          setCargandoCatalogo(false)
          return
        }
        if (p.empresa_id !== empresaId) {
          setError('El presupuesto no pertenece a esta empresa')
          setCargandoCatalogo(false)
          return
        }
        if (p.estado !== 'borrador') {
          setError(`Solo se pueden editar presupuestos en estado "borrador". Este está en "${p.estado}". Duplicalo para crear una versión nueva.`)
          setCargandoCatalogo(false)
          return
        }
        // Precargar header
        setHeader({
          modalidad: p.modalidad || 'suscripcion',
          ocultar_total: p.ocultar_total ?? false,
          edificio_id: p.edificio_id || '',
          facturacion_con_rut: p.facturacion_con_rut ?? null,
          fecha: p.fecha || new Date().toISOString().slice(0, 10),
          validez_dias: p.validez_dias || 15,
          condiciones_pago: p.condiciones_pago || '',
          condiciones_entrega: p.condiciones_entrega || '',
          notas_internas: p.notas_internas || '',
          notas_cliente: p.notas_cliente || '',
          descuento_pct: Number(p.descuento_pct) || 0,
        })
        // Precargar items ordenados
        const itemsOrdenados = (p.items || []).sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))
        setItems(itemsOrdenados.map(it => ({
          tipo: it.tipo,
          producto_id: it.producto_id,
          kit_codigo: it.kit_codigo,
          descripcion: it.descripcion,
          cantidad: Number(it.cantidad),
          precio_unitario: Number(it.precio_unitario),
          descuento_pct: Number(it.descuento_pct),
          iva_pct: Number(it.iva_pct),
          direccion_id: it.direccion_id,
        })))
        setNumeroEditando(p.numero)
        setBorradorRestaurado(false)  // no confundir con "borrador de localStorage"
      }

      setCargandoCatalogo(false)
    })()
  }, [empresaId, editarId])

  const totales = useMemo(() => {
    let subtotal = 0
    let iva = 0
    items.forEach(it => {
      const cant = Number(it.cantidad) || 0
      const precio = Number(it.precio_unitario) || 0
      const desc = Number(it.descuento_pct) || 0
      const sub = cant * precio * (1 - desc / 100)
      subtotal += sub
      const ivaPct = Number(it.iva_pct) || 22
      iva += sub - (sub / (1 + ivaPct / 100))
    })
    const descGlobal = Number(header.descuento_pct) || 0
    const subTrasDescGlobal = subtotal * (1 - descGlobal / 100)
    const ivaTrasDescGlobal = iva * (1 - descGlobal / 100)
    return {
      subtotal,
      iva_total: ivaTrasDescGlobal,
      total: subTrasDescGlobal,
    }
  }, [items, header.descuento_pct])

  function setH(k, v) { setHeader(h => ({ ...h, [k]: v })) }

  function agregarProducto(producto) {
    if (items.some(i => i.tipo === 'producto' && i.producto_id === producto.id)) {
      // ya está, foco en su cantidad
      return
    }
    setItems(prev => [...prev, {
      tipo: 'producto',
      producto_id: producto.id,
      kit_codigo: null,
      descripcion: `${producto.marca || ''} · ${producto.nombre} · ${producto.presentacion || ''}`.trim().replace(/^\s*·\s*/, ''),
      cantidad: 1,
      precio_unitario: Number(producto.precio_publico) || 0,
      descuento_pct: 0,
      iva_pct: producto.iva_pct || 22,
      direccion_id: null,
      orden: prev.length,
    }])
  }

  function agregarPersonalizado() {
    setItems(prev => [...prev, {
      tipo: 'personalizado',
      producto_id: null,
      kit_codigo: null,
      descripcion: '',
      cantidad: 1,
      precio_unitario: 0,
      descuento_pct: 0,
      iva_pct: 22,
      direccion_id: null,
      orden: prev.length,
    }])
  }

  function updateItem(idx, patch) {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it))
  }

  function removeItem(idx) {
    setItems(prev => prev.filter((_, i) => i !== idx))
  }

  async function finalizar() {
    setError(null)
    if (items.length === 0) {
      setError('Tenés que agregar al menos un item')
      return
    }
    setGuardando(true)

    let pres  // { id } — se llena vía INSERT (nuevo) o vía editarId (edición)

    if (editarId) {
      // MODO EDICIÓN: UPDATE del presupuesto existente. Mantiene mismo número.
      // Solo se llega acá si el estado es 'borrador' (validado al cargar).
      const { error: uErr } = await supabase.from('presupuestos').update({
        modalidad: header.modalidad,
        ocultar_total: !!header.ocultar_total,
        edificio_id: header.edificio_id || null,
        facturacion_con_rut: header.facturacion_con_rut ?? null,
        fecha: header.fecha,
        validez_dias: Number(header.validez_dias),
        condiciones_pago: header.condiciones_pago || null,
        condiciones_entrega: header.condiciones_entrega || null,
        notas_internas: header.notas_internas || null,
        notas_cliente: header.notas_cliente || null,
        subtotal: round2(totales.subtotal),
        descuento_pct: Number(header.descuento_pct) || 0,
        iva_total: round2(totales.iva_total),
        total: round2(totales.total),
        updated_at: new Date().toISOString(),
      }).eq('id', editarId)

      if (uErr) {
        setError(uErr.message)
        setGuardando(false)
        return
      }
      // Borrar TODOS los items viejos y re-insertar (más simple que diff)
      const { error: dErr } = await supabase.from('presupuestos_items').delete().eq('presupuesto_id', editarId)
      if (dErr) {
        setError('Error limpiando items viejos: ' + dErr.message)
        setGuardando(false)
        return
      }
      pres = { id: editarId }
    } else {
      // MODO CREACIÓN: número nuevo + INSERT
      const { data: numeroData, error: nErr } = await supabase.rpc('next_presupuesto_numero')
      if (nErr) {
        setError(nErr.message)
        setGuardando(false)
        return
      }
      const numero = numeroData

      const { data: { user } } = await supabase.auth.getUser()
      const { data: presData, error: pErr } = await supabase.from('presupuestos').insert({
        numero,
        empresa_id: empresaId,
        modalidad: header.modalidad,
        ocultar_total: !!header.ocultar_total,
        edificio_id: header.edificio_id || null,
        facturacion_con_rut: header.facturacion_con_rut ?? null,
        fecha: header.fecha,
        validez_dias: Number(header.validez_dias),
        estado: 'borrador',
        condiciones_pago: header.condiciones_pago || null,
        condiciones_entrega: header.condiciones_entrega || null,
        notas_internas: header.notas_internas || null,
        notas_cliente: header.notas_cliente || null,
        subtotal: round2(totales.subtotal),
        descuento_pct: Number(header.descuento_pct) || 0,
        iva_total: round2(totales.iva_total),
        total: round2(totales.total),
        created_by: user?.id || null,
      }).select('id').single()

      if (pErr) {
        setError(pErr.message)
        setGuardando(false)
        return
      }
      pres = presData
    }

    // 3) Insertar items
    const itemsPayload = items.map((it, idx) => {
      const cant = Number(it.cantidad) || 0
      const precio = Number(it.precio_unitario) || 0
      const desc = Number(it.descuento_pct) || 0
      const subtotal = cant * precio * (1 - desc / 100)
      return {
        presupuesto_id: pres.id,
        tipo: it.tipo,
        producto_id: it.producto_id,
        kit_codigo: it.kit_codigo,
        descripcion: it.descripcion || null,
        cantidad: cant,
        precio_unitario: precio,
        descuento_pct: desc,
        subtotal: round2(subtotal),
        iva_pct: it.iva_pct || 22,
        direccion_id: it.direccion_id || null,
        orden: idx,
      }
    })
    const { error: iErr } = await supabase.from('presupuestos_items').insert(itemsPayload)
    if (iErr) {
      setError(iErr.message)
      setGuardando(false)
      return
    }

    // 4) Generar HTML brandeado (best effort)
    try {
      await supabase.functions.invoke('generar-presupuesto-html', { body: { presupuesto_id: pres.id } })
    } catch (err) {
      console.warn('Generación de HTML falló (no es bloqueante):', err)
    }

    // 5) Limpiar borrador — el presupuesto ya existe en DB
    if (draftKey) {
      try { localStorage.removeItem(draftKey) } catch {}
    }

    // 6) Ir al detalle
    nav(`/admin/presupuestos/${pres.id}`)
  }

  function descartarBorrador() {
    if (!draftKey) return
    if (!confirm('¿Descartar el borrador guardado? Perdés todo lo cargado.')) return
    try { localStorage.removeItem(draftKey) } catch {}
    // Reset a estado inicial
    setPaso(0)
    setHeader({
      modalidad: 'suscripcion',
      ocultar_total: false,
      fecha: new Date().toISOString().slice(0, 10),
      validez_dias: 15,
      condiciones_pago: 'Pago contado contra entrega',
      condiciones_entrega: 'Entregas mensuales en su dirección, en el día de su barrio',
      notas_internas: '',
      notas_cliente: '',
      descuento_pct: 0,
    })
    setItems([])
    setBorradorRestaurado(false)
  }

  if (cargandoCatalogo) {
    return <AdminLayout title="Nuevo presupuesto"><div style={{ padding: 40, textAlign: 'center' }}>Cargando catálogo…</div></AdminLayout>
  }

  if (!empresa) {
    return <AdminLayout title="Nuevo presupuesto"><div style={{ padding: 24 }}>Empresa no encontrada</div></AdminLayout>
  }

  return (
    <AdminLayout title={editarId && numeroEditando ? `Editando ${numeroEditando} · ${empresa.razon_social}` : `Nuevo presupuesto · ${empresa.razon_social}`}>
      <Estilos />
      <div className="pn-page">
        <button className="pn-back" onClick={() => nav(`/admin/empresas/${empresaId}`)}>← Volver a {empresa.razon_social}</button>

        {borradorRestaurado && draftSaved?.guardadoEn && (
          <div style={{
            marginTop: 12, padding: '10px 14px', background: '#fef3c7', border: '1px solid #fde68a',
            borderRadius: 8, color: '#78350f', fontSize: 13, display: 'flex',
            alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
          }}>
            <span>
              📄 <strong>Borrador restaurado</strong> — última edición {fmtHace(draftSaved.guardadoEn)}.
              Podés seguir donde estabas.
            </span>
            <button onClick={descartarBorrador} style={{
              background: 'transparent', border: '1px solid #a16207', color: '#78350f',
              borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}>
              Descartar y empezar de cero
            </button>
          </div>
        )}

        <header className="pn-head">
          <h1>{editarId ? `Editando presupuesto ${numeroEditando || ''}` : 'Nuevo presupuesto'}</h1>
          <p>Empresa: <strong>{empresa.razon_social}</strong> {empresa.rut && <>· RUT {empresa.rut}</>}</p>
          {editarId && (
            <p style={{ fontSize: 12, color: '#78350f', background: '#fef3c7', border: '1px solid #fde68a', padding: '6px 10px', borderRadius: 6, marginTop: 8 }}>
              ✏️ Modo edición · el número <strong>{numeroEditando}</strong> se mantiene. Los cambios sobrescriben el presupuesto original.
            </p>
          )}
        </header>

        <div className="pn-stepper">
          {PASOS.map((nom, idx) => (
            <div key={idx} className={`pn-step${idx === paso ? ' active' : ''}${idx < paso ? ' done' : ''}`}>
              <span className="pn-step-num">{idx + 1}</span>
              <span>{nom}</span>
            </div>
          ))}
        </div>

        <div className="pn-content">
          {paso === 0 && <PasoHeader header={header} setH={setH} empresa={empresa} edificios={edificios} />}
          {paso === 1 && (
            <PasoItems
              items={items}
              productos={productos}
              direcciones={direcciones}
              updateItem={updateItem}
              removeItem={removeItem}
              agregarProducto={agregarProducto}
              agregarPersonalizado={agregarPersonalizado}
            />
          )}
          {paso === 2 && (
            <PasoRevision
              empresa={empresa}
              header={header}
              items={items}
              totales={totales}
            />
          )}
        </div>

        {error && <div className="pn-error">⚠ {error}</div>}

        <div className="pn-foot">
          <button className="pn-btn-secondary" onClick={() => setPaso(p => Math.max(0, p - 1))} disabled={paso === 0 || guardando}>
            ← Atrás
          </button>
          <div className="pn-spacer" />
          {paso < 2 ? (
            <button
              className="pn-btn-primary"
              onClick={() => setPaso(p => p + 1)}
              disabled={paso === 1 && items.length === 0}
            >
              Siguiente →
            </button>
          ) : (
            <button className="pn-btn-primary" onClick={finalizar} disabled={guardando}>
              {guardando ? 'Guardando…' : (editarId ? '✓ Guardar cambios' : '✓ Crear presupuesto y ver detalle')}
            </button>
          )}
        </div>
      </div>
    </AdminLayout>
  )
}

function PasoHeader({ header, setH, empresa, edificios }) {
  function setModalidad(nueva) {
    setH('modalidad', nueva)
    // Auto-ajustar condiciones de entrega según modalidad
    if (nueva === 'suscripcion') {
      setH('condiciones_entrega', 'Entregas mensuales en su dirección, en el día de su barrio')
    } else {
      setH('condiciones_entrega', 'Entrega única en la fecha a coordinar')
    }
  }

  return (
    <div className="pn-card">
      <div className="pn-modalidad">
        <span className="pn-modalidad-label">¿Qué tipo de servicio es?</span>
        <div className="pn-modalidad-opts">
          <label className={`pn-modalidad-opt${header.modalidad === 'suscripcion' ? ' on' : ''}`}>
            <input type="radio" name="modalidad" value="suscripcion" checked={header.modalidad === 'suscripcion'} onChange={() => setModalidad('suscripcion')} />
            <div>
              <strong>🔄 Servicio mensual</strong>
              <span>Suscripción recurrente — se entrega y cobra cada mes</span>
            </div>
          </label>
          <label className={`pn-modalidad-opt${header.modalidad === 'compra_unica' ? ' on' : ''}`}>
            <input type="radio" name="modalidad" value="compra_unica" checked={header.modalidad === 'compra_unica'} onChange={() => setModalidad('compra_unica')} />
            <div>
              <strong>🛍 Compra única</strong>
              <span>Pedido one-shot — una sola entrega y un solo cobro</span>
            </div>
          </label>
        </div>
      </div>

      {empresa?.tipo === 'administradora' && (
        <Field label="Edificio (de esta administradora)">
          <select value={header.edificio_id || ''} onChange={e => setH('edificio_id', e.target.value)}>
            <option value="">— Sin edificio específico —</option>
            {(edificios || []).map(ed => (
              <option key={ed.id} value={ed.id}>
                {ed.nombre}{ed.razon_social ? ` · ${ed.razon_social}` : ''}{ed.rut ? ` · RUT ${ed.rut}` : ''}
              </option>
            ))}
          </select>
          {(edificios || []).length === 0 && (
            <div style={{ fontSize: 12, color: '#B45309', marginTop: 4 }}>Esta administradora no tiene edificios cargados — cargalos en la ficha de la empresa → pestaña Edificios.</div>
          )}
        </Field>
      )}
      {empresa?.tipo === 'administradora' && header.edificio_id && (() => {
        const edSel = (edificios || []).find(e => e.id === header.edificio_id)
        if (!edSel) return null
        const conRutEff = (header.facturacion_con_rut === true || header.facturacion_con_rut === false)
          ? header.facturacion_con_rut : !!edSel.rut
        return (
          <div style={{ background: '#F0FDFA', border: '1px solid #BBF7D0', borderRadius: 8, padding: '12px 14px', margin: '0 0 14px' }}>
            <div style={{ fontSize: 12.5, color: '#134E4A', marginBottom: 8 }}>
              Presupuesto para: <strong>{edSel.nombre}</strong>{edSel.direccion ? ` · ${edSel.direccion}${edSel.apto ? ' · ' + edSel.apto : ''}` : ''}
              {edSel.razon_social ? ` · ${edSel.razon_social}` : ''}{edSel.rut ? ` · RUT ${edSel.rut}` : ' · (sin RUT)'} · Administradora {empresa.razon_social}
            </div>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#1A3461', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>¿Cómo se factura este edificio?</div>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginRight: 18, fontSize: 13, cursor: 'pointer' }}>
              <input type="radio" name="conrut" checked={conRutEff === true} onChange={() => setH('facturacion_con_rut', true)} /> Con RUT (e-Factura)
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
              <input type="radio" name="conrut" checked={conRutEff === false} onChange={() => setH('facturacion_con_rut', false)} /> Sin RUT (consumidor final)
            </label>
            {conRutEff === true && !edSel.rut && (
              <div style={{ fontSize: 12, color: '#B45309', marginTop: 6 }}>⚠ Elegiste con RUT pero el edificio no tiene RUT cargado. Cargalo en la ficha de la empresa → Edificios, o pasá a sin RUT.</div>
            )}
          </div>
        )
      })()}
      <div className="pn-grid-2">
        <Field label="Fecha de emisión">
          <input type="date" value={header.fecha} onChange={e => setH('fecha', e.target.value)} />
        </Field>
        <Field label="Validez (días)">
          <input type="number" min="1" value={header.validez_dias} onChange={e => setH('validez_dias', e.target.value)} />
        </Field>
      </div>

      <Field label="Condiciones de pago">
        <input value={header.condiciones_pago} onChange={e => setH('condiciones_pago', e.target.value)} />
      </Field>
      <Field label="Condiciones de entrega">
        <input value={header.condiciones_entrega} onChange={e => setH('condiciones_entrega', e.target.value)} />
      </Field>
      <Field label="Notas internas (NO van al cliente)">
        <textarea rows={2} value={header.notas_internas} onChange={e => setH('notas_internas', e.target.value)} />
      </Field>
      <Field label="Notas al cliente (van en el PDF)">
        <textarea rows={3} value={header.notas_cliente} onChange={e => setH('notas_cliente', e.target.value)} placeholder="Ej: Esta cotización contempla 4 entregas mensuales. Si querés alternativas en algún producto, avisanos." />
      </Field>
      <Field label="Descuento global (%)">
        <input type="number" min="0" max="100" step="0.5" value={header.descuento_pct} onChange={e => setH('descuento_pct', e.target.value)} />
      </Field>
      <Field label="Total en el PDF">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!header.ocultar_total} onChange={e => setH('ocultar_total', e.target.checked)} />
          Ocultar total (lista de precios — sin el bloque Total/IVA)
        </label>
      </Field>
    </div>
  )
}

function PasoItems({ items, productos, direcciones, updateItem, removeItem, agregarProducto, agregarPersonalizado }) {
  const [busqProd, setBusqProd] = useState('')
  const prodFilt = useMemo(() => {
    if (!busqProd) return productos
    const q = busqProd.toLowerCase()
    return productos.filter(p =>
      p.nombre?.toLowerCase().includes(q) ||
      p.marca?.toLowerCase().includes(q) ||
      p.sku?.toLowerCase().includes(q) ||
      p.presentacion?.toLowerCase().includes(q)
    )
  }, [productos, busqProd])

  return (
    <div className="pn-card">
      <div className="pn-items-header">
        <h3>Items del presupuesto</h3>
        <div className="pn-items-actions">
          <button
            className="pn-btn-secondary pn-btn-sm"
            onClick={agregarPersonalizado}
            title="Sumá al presupuesto un producto que no está en tu catálogo (lo salís a conseguir después)"
            style={{ background: '#FEF3C7', borderColor: '#FDE68A', color: '#78350F' }}
          >
            🔍 + Producto a conseguir
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="pn-items-empty">
          <p>Todavía no agregaste items. Buscá productos abajo o sumá un kit / línea personalizada.</p>
        </div>
      ) : (
        <table className="pn-items-table">
          <thead>
            <tr>
              <th>Descripción</th>
              <th>Cant.</th>
              <th>Precio unit. (IVA inc.)</th>
              <th>Desc. %</th>
              <th>Subtotal</th>
              {direcciones.length > 1 && <th>Dirección</th>}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, idx) => {
              const cant = Number(it.cantidad) || 0
              const precio = Number(it.precio_unitario) || 0
              const desc = Number(it.descuento_pct) || 0
              const sub = cant * precio * (1 - desc / 100)
              return (
                <tr key={idx}>
                  <td>
                    {it.tipo === 'personalizado' ? (
                      <>
                        <input
                          value={it.descripcion}
                          onChange={e => updateItem(idx, { descripcion: e.target.value })}
                          placeholder="Ej: Papel higiénico Elite doble hoja 24u"
                          style={{ width: '100%', minWidth: 260 }}
                        />
                        <div style={{
                          display: 'inline-block', marginTop: 4,
                          background: '#FEF3C7', color: '#78350F',
                          fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
                          border: '1px solid #FDE68A',
                        }}>
                          🔍 A conseguir · fuera de catálogo
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="pn-item-desc">{it.descripcion}</div>
                        <div className="pn-item-tag">{it.tipo === 'kit' ? '🎁 Kit' : '📦 Producto'}</div>
                      </>
                    )}
                  </td>
                  <td>
                    <input type="number" min="0.01" step="0.5" value={it.cantidad} onChange={e => updateItem(idx, { cantidad: e.target.value })} style={{ width: 70 }} />
                  </td>
                  <td>
                    <input type="number" min="0" step="0.01" value={it.precio_unitario} onChange={e => updateItem(idx, { precio_unitario: e.target.value })} style={{ width: 100 }} />
                  </td>
                  <td>
                    <input type="number" min="0" max="100" step="0.5" value={it.descuento_pct} onChange={e => updateItem(idx, { descuento_pct: e.target.value })} style={{ width: 60 }} />
                  </td>
                  <td className="pn-num"><strong>${sub.toLocaleString('es-UY', { maximumFractionDigits: 2 })}</strong></td>
                  {direcciones.length > 1 && (
                    <td>
                      <select value={it.direccion_id || ''} onChange={e => updateItem(idx, { direccion_id: e.target.value || null })} style={{ width: 140 }}>
                        <option value="">Todas</option>
                        {direcciones.map(d => <option key={d.id} value={d.id}>{d.alias}</option>)}
                      </select>
                    </td>
                  )}
                  <td>
                    <button className="pn-btn-link pn-danger" onClick={() => removeItem(idx)}>✕</button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      <div className="pn-catalog">
        <h4>Agregar desde catálogo</h4>
        <div className="pn-catalog-hint">
          Los presupuestos B2B se arman a medida con productos sueltos + líneas personalizadas. Los kits estándar son solo para B2C.
        </div>

        <div className="pn-catalog-section">
          <strong>Productos sueltos</strong>
          <input
            className="pn-busqueda"
            type="search"
            value={busqProd}
            onChange={e => setBusqProd(e.target.value)}
            placeholder="Buscar producto por nombre, marca o SKU..."
          />
          <div className="pn-prods-list">
            {prodFilt.map(p => (
              <button key={p.id} className="pn-prod-row" onClick={() => agregarProducto(p)}>
                <span className="pn-prod-name">
                  <strong>{p.nombre}</strong>
                  {p.marca && <span className="pn-prod-marca"> · {p.marca}</span>}
                  {p.presentacion && <span className="pn-prod-pres"> · {p.presentacion}</span>}
                </span>
                <span className="pn-prod-precio">${Number(p.precio_publico).toLocaleString('es-UY')}</span>
              </button>
            ))}
            {prodFilt.length === 0 && <div style={{ padding: 12, color: '#64748B', fontSize: 13 }}>Sin resultados</div>}
          </div>
          <div style={{ fontSize: 11.5, color: '#64748B', marginTop: 6 }}>
            {busqProd
              ? `${prodFilt.length} de ${productos.length} producto${productos.length === 1 ? '' : 's'}`
              : `${productos.length} producto${productos.length === 1 ? '' : 's'} activos · scrolleá la lista`}
          </div>
        </div>
      </div>
    </div>
  )
}

function PasoRevision({ empresa, header, items, totales }) {
  return (
    <div className="pn-card">
      <div className="pn-rev-grid">
        <div>
          <h4>Cliente</h4>
          <div className="pn-rev-block">
            <strong>{empresa.razon_social}</strong>
            {empresa.rut && <div>RUT {empresa.rut}</div>}
          </div>
        </div>
        <div>
          <h4>Datos del presupuesto</h4>
          <div className="pn-rev-block">
            <div>Fecha: {new Date(header.fecha).toLocaleDateString('es-UY')}</div>
            <div>Validez: {header.validez_dias} días</div>
            <div>Pago: {header.condiciones_pago}</div>
            <div>Entrega: {header.condiciones_entrega}</div>
          </div>
        </div>
      </div>

      <h4>Items ({items.length})</h4>
      <table className="pn-rev-table">
        <thead><tr><th>Descripción</th><th>Cant.</th><th>Precio</th><th>Desc.</th><th>Subtotal</th></tr></thead>
        <tbody>
          {items.map((it, idx) => {
            const sub = Number(it.cantidad) * Number(it.precio_unitario) * (1 - Number(it.descuento_pct || 0) / 100)
            return (
              <tr key={idx}>
                <td>{it.descripcion}</td>
                <td className="pn-num">{Number(it.cantidad)}</td>
                <td className="pn-num">${Number(it.precio_unitario).toLocaleString('es-UY')}</td>
                <td className="pn-num">{Number(it.descuento_pct || 0)}%</td>
                <td className="pn-num"><strong>${sub.toLocaleString('es-UY', { maximumFractionDigits: 2 })}</strong></td>
              </tr>
            )
          })}
        </tbody>
      </table>

      <div className="pn-rev-totales">
        <div>Subtotal: <strong>${totales.subtotal.toLocaleString('es-UY', { maximumFractionDigits: 2 })}</strong></div>
        {Number(header.descuento_pct) > 0 && <div>Descuento global ({header.descuento_pct}%)</div>}
        <div>IVA incluido: ${totales.iva_total.toLocaleString('es-UY', { maximumFractionDigits: 2 })}</div>
        <div className="pn-total"><strong>Total: ${totales.total.toLocaleString('es-UY', { maximumFractionDigits: 2 })}</strong></div>
      </div>

      {header.notas_cliente && (
        <div className="pn-rev-notas">
          <strong>Notas al cliente:</strong>
          <p>{header.notas_cliente}</p>
        </div>
      )}
    </div>
  )
}

function Field({ label, children }) {
  return (
    <label className="pn-field">
      <span>{label}</span>
      {children}
    </label>
  )
}

function round2(n) {
  return Math.round(n * 100) / 100
}

function Estilos() {
  return (
    <style>{`
      .pn-page { max-width: 1100px; margin: 0 auto; padding: 24px; font-family: Inter, system-ui, sans-serif; color: #1A3461; }
      .pn-back { background: transparent; border: none; color: #64748B; font-size: 13px; cursor: pointer; padding: 4px 0; margin-bottom: 8px; font-family: inherit; }
      .pn-back:hover { color: #1A3461; }

      .pn-head h1 { margin: 0 0 4px; font-size: 22px; font-weight: 900; letter-spacing: -0.02em; }
      .pn-head p { margin: 0 0 20px; font-size: 13.5px; color: #64748B; }

      .pn-stepper { display: flex; gap: 4px; margin-bottom: 20px; }
      .pn-step { flex: 1; display: flex; align-items: center; gap: 10px; padding: 12px 16px; background: #F1F5F9; border-radius: 8px; font-size: 13px; font-weight: 600; color: #64748B; }
      .pn-step.active { background: #1A3461; color: #fff; }
      .pn-step.done { background: #DCFCE7; color: #14532D; }
      .pn-step-num { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 50%; background: rgba(255,255,255,0.2); font-weight: 800; font-size: 12px; }
      .pn-step.done .pn-step-num { background: #16A34A; color: #fff; }
      .pn-step:not(.active):not(.done) .pn-step-num { background: #fff; color: #64748B; border: 1px solid #E2E8F0; }

      .pn-content { min-height: 200px; margin-bottom: 18px; }
      .pn-card { background: #fff; border: 1px solid #E2E8F0; border-radius: 12px; padding: 22px; }

      .pn-modalidad { margin-bottom: 18px; padding: 14px; background: #F8FAFC; border-radius: 10px; border: 1px solid #E2E8F0; }
      .pn-modalidad-label { display: block; font-size: 12px; font-weight: 700; color: #475569; margin-bottom: 10px; text-transform: uppercase; letter-spacing: 0.05em; }
      .pn-modalidad-opts { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
      @media (max-width: 600px) { .pn-modalidad-opts { grid-template-columns: 1fr; } }
      .pn-modalidad-opt {
        display: flex; gap: 12px; align-items: flex-start; padding: 12px 14px;
        background: #fff; border: 1.5px solid #E2E8F0; border-radius: 10px;
        cursor: pointer; transition: all 0.12s;
      }
      .pn-modalidad-opt:hover { border-color: #00A8B5; background: #F0FDFA; }
      .pn-modalidad-opt.on { border-color: #00A8B5; background: #F0FDFA; box-shadow: 0 0 0 3px rgba(0,168,181,0.12); }
      .pn-modalidad-opt input[type="radio"] { width: 18px; height: 18px; margin-top: 2px; cursor: inherit; accent-color: #00A8B5; flex-shrink: 0; }
      .pn-modalidad-opt > div { flex: 1; }
      .pn-modalidad-opt strong { display: block; font-size: 14px; color: #1A3461; line-height: 1.2; margin-bottom: 3px; }
      .pn-modalidad-opt span { font-size: 12px; color: #64748B; line-height: 1.4; }

      .pn-field { display: block; margin-bottom: 14px; }
      .pn-field > span { display: block; font-size: 12px; font-weight: 700; color: #475569; margin-bottom: 5px; }
      .pn-field input, .pn-field select, .pn-field textarea {
        width: 100%; padding: 9px 11px; border-radius: 8px; border: 1.5px solid #E2E8F0;
        font-size: 13.5px; font-family: inherit; color: #1A3461; background: #fff; box-sizing: border-box;
      }
      .pn-field textarea { resize: vertical; min-height: 60px; }
      .pn-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }

      .pn-items-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
      .pn-items-header h3 { margin: 0; font-size: 16px; font-weight: 800; color: #1A3461; }
      .pn-items-actions { display: flex; gap: 6px; }

      .pn-items-empty { padding: 24px; text-align: center; background: #F8FAFC; border: 1px dashed #E2E8F0; border-radius: 8px; color: #64748B; margin-bottom: 14px; }

      .pn-items-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
      .pn-items-table th { background: #F8FAFC; padding: 9px; font-size: 11px; font-weight: 700; color: #64748B; text-transform: uppercase; letter-spacing: 0.05em; text-align: left; border-bottom: 1px solid #E2E8F0; }
      .pn-items-table td { padding: 8px 9px; border-bottom: 1px solid #F1F5F9; vertical-align: middle; font-size: 13px; }
      .pn-items-table input, .pn-items-table select { padding: 6px 8px; border: 1.5px solid #E2E8F0; border-radius: 6px; font-size: 13px; font-family: inherit; color: #1A3461; background: #fff; }
      .pn-item-desc { font-weight: 600; font-size: 13px; }
      .pn-item-tag { font-size: 10.5px; color: #64748B; margin-top: 2px; }

      .pn-catalog { border-top: 1px solid #E2E8F0; padding-top: 16px; margin-top: 6px; }
      .pn-catalog h4 { margin: 0 0 6px; font-size: 13px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.05em; }
      .pn-catalog-hint { font-size: 12px; color: #64748B; margin-bottom: 12px; line-height: 1.45; }
      .pn-catalog-section { margin-bottom: 14px; }
      .pn-catalog-section > strong { display: block; font-size: 12px; color: #1A3461; margin-bottom: 6px; }
      .pn-busqueda { width: 100%; padding: 8px 11px; border-radius: 8px; border: 1.5px solid #E2E8F0; font-size: 13px; font-family: inherit; color: #1A3461; background: #fff; margin-bottom: 8px; box-sizing: border-box; }
      .pn-prods-list { max-height: 480px; overflow-y: auto; border: 1px solid #E2E8F0; border-radius: 8px; background: #fff; }
      .pn-prod-row { display: flex; justify-content: space-between; align-items: center; width: 100%; padding: 8px 12px; background: transparent; border: none; border-bottom: 1px solid #F1F5F9; font-family: inherit; font-size: 13px; cursor: pointer; text-align: left; color: #1A3461; }
      .pn-prod-row:hover { background: #F8FAFC; }
      .pn-prod-row:last-child { border-bottom: none; }
      .pn-prod-name { flex: 1; }
      .pn-prod-marca, .pn-prod-pres { color: #64748B; font-size: 12px; font-weight: 400; }
      .pn-prod-precio { font-weight: 700; color: #00A8B5; font-variant-numeric: tabular-nums; }

      .pn-rev-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; margin-bottom: 16px; }
      .pn-rev-grid h4 { margin: 0 0 8px; font-size: 11px; font-weight: 700; color: #64748B; text-transform: uppercase; letter-spacing: 0.06em; }
      .pn-rev-block { background: #F8FAFC; padding: 12px; border-radius: 8px; font-size: 13px; line-height: 1.6; }

      .pn-rev-table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
      .pn-rev-table th { background: #F1F5F9; padding: 9px; font-size: 11.5px; font-weight: 700; color: #64748B; text-transform: uppercase; letter-spacing: 0.05em; text-align: left; }
      .pn-rev-table td { padding: 9px; border-bottom: 1px solid #F1F5F9; font-size: 13px; }

      .pn-rev-totales { text-align: right; padding: 14px 16px; background: #1A3461; color: #fff; border-radius: 8px; font-size: 13.5px; line-height: 1.8; }
      .pn-rev-totales .pn-total { font-size: 16px; font-weight: 800; padding-top: 8px; margin-top: 8px; border-top: 1px solid rgba(255,255,255,0.15); }
      .pn-rev-notas { background: #F0FDFA; padding: 12px 16px; border-left: 3px solid #00A8B5; border-radius: 0 8px 8px 0; margin-top: 14px; font-size: 13px; color: #134E4A; }
      .pn-rev-notas strong { display: block; margin-bottom: 4px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; color: #1A3461; }
      .pn-rev-notas p { margin: 0; line-height: 1.5; }

      .pn-num { text-align: right; font-variant-numeric: tabular-nums; }

      .pn-foot { display: flex; align-items: center; gap: 10px; }
      .pn-spacer { flex: 1; }
      .pn-btn-primary, .pn-btn-secondary, .pn-btn-link {
        padding: 10px 18px; border-radius: 8px; font-size: 13.5px; font-weight: 700;
        font-family: inherit; cursor: pointer; transition: all 0.15s; border: none;
      }
      .pn-btn-primary { background: #1A3461; color: #fff; }
      .pn-btn-primary:hover { background: #1A3461; }
      .pn-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
      .pn-btn-secondary { background: #fff; color: #475569; border: 1.5px solid #E2E8F0; }
      .pn-btn-secondary:hover { border-color: #1A3461; color: #1A3461; }
      .pn-btn-secondary:disabled { opacity: 0.5; cursor: not-allowed; }
      .pn-btn-sm { padding: 6px 12px; font-size: 12px; }
      .pn-btn-link { background: transparent; padding: 4px 8px; color: #DC2626; font-size: 14px; }
      .pn-danger:hover { background: #FEE2E2; }

      .pn-error { background: #FEE2E2; border-left: 3px solid #DC2626; padding: 10px 14px; border-radius: 0 8px 8px 0; color: #991B1B; font-size: 13px; margin-bottom: 12px; }
    `}</style>
  )
}
